import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { User } from '../types';
import { Button } from './Button';
import { Input } from './Input';
import { Colors, Fonts, Shadows, Typography } from '../constants';
import { authService } from '../services/auth';
import { imageService } from '../services/images';
import { useUser, useDialog } from '../contexts';

interface EditProfileFormProps {
  /** Hide the in-form heading when the screen header already shows it (mobile) */
  showTitle?: boolean;
  user: User;
  onSave: () => void;
  onCancel: () => void;
}

export const EditProfileForm: React.FC<EditProfileFormProps> = ({ user, onSave, onCancel, showTitle = true }) => {
  const { t } = useTranslation();
  const { refreshUser } = useUser();
  const dialog = useDialog();

  const [firstName, setFirstName] = useState(user.first_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');
  const [avatarPublicId, setAvatarPublicId] = useState<string | null>(user.avatar_url || null);
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const isDirty =
    firstName !== (user.first_name || '') ||
    lastName !== (user.last_name || '') ||
    avatarPublicId !== (user.avatar_url || null);

  const handleFirstNameChange = (text: string) => {
    setFirstName(text);
    if (firstNameError) setFirstNameError('');
  };

  const handleLastNameChange = (text: string) => {
    setLastName(text);
    if (lastNameError) setLastNameError('');
  };

  const handlePickAvatar = async () => {
    try {
      const result = await imageService.pickImages(1);
      if (result.canceled || !result.assets?.length) return;

      setUploadingAvatar(true);
      const publicId = await imageService.uploadSingleImage(
        result.assets[0].uri,
        'clubswap/avatars',
      );
      setAvatarPublicId(publicId);
    } catch (error) {
      console.error('Avatar upload failed:', error);
      dialog.alert(
        t('screens.profile.profileUpdateFailed'),
        String(error instanceof Error ? error.message : error),
        'error'
      );
    } finally {
      setUploadingAvatar(false);
    }
  };

  const validate = (): boolean => {
    let valid = true;
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    if (!trimmedFirst) {
      setFirstNameError(t('screens.profile.firstNameRequired'));
      valid = false;
    } else if (trimmedFirst.length > 50) {
      setFirstNameError(t('screens.profile.firstNameTooLong'));
      valid = false;
    }

    if (!trimmedLast) {
      setLastNameError(t('screens.profile.lastNameRequired'));
      valid = false;
    } else if (trimmedLast.length > 50) {
      setLastNameError(t('screens.profile.lastNameTooLong'));
      valid = false;
    }

    return valid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const updates: { first_name?: string; last_name?: string; avatar_url?: string } = {};

      if (firstName.trim() !== (user.first_name || '')) {
        updates.first_name = firstName.trim();
      }
      if (lastName.trim() !== (user.last_name || '')) {
        updates.last_name = lastName.trim();
      }
      if (avatarPublicId !== (user.avatar_url || null)) {
        updates.avatar_url = avatarPublicId || undefined;
      }

      if (Object.keys(updates).length > 0) {
        await authService.updateProfile(updates);
        await refreshUser();
      }

      onSave();
    } catch (error) {
      console.error('Profile update failed:', error);
      dialog.alert(
        t('screens.profile.profileUpdateFailed'),
        String(error instanceof Error ? error.message : error),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const avatarUri = avatarPublicId
    ? imageService.getCircularThumbnailUrl(avatarPublicId)
    : null;

  return (
    <View style={styles.container}>
      {showTitle ? <Text style={styles.title}>{t('screens.profile.editProfileTitle')}</Text> : null}

      {/* Avatar */}
      <TouchableOpacity
        style={styles.avatarContainer}
        onPress={handlePickAvatar}
        disabled={uploadingAvatar}
        accessibilityLabel={t('screens.profile.changeAvatar')}
      >
        <View style={styles.avatar}>
          {uploadingAvatar ? (
            <ActivityIndicator size="small" color={Colors.textDark} />
          ) : avatarUri ? (
            <Image
              source={{ uri: avatarUri }}
              style={styles.avatarImage}
            />
          ) : (
            <Text style={styles.avatarText}>
              {firstName?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
            </Text>
          )}
        </View>
        <View style={styles.cameraOverlay}>
          <Ionicons name="camera" size={14} color={Colors.white} />
        </View>
      </TouchableOpacity>

      {/* Form fields */}
      <View style={styles.form}>
        <Input
          label={t('screens.profile.firstName')}
          value={firstName}
          onChangeText={handleFirstNameChange}
          error={firstNameError}
          maxLength={50}
          autoCapitalize="words"
        />

        <Input
          label={t('screens.profile.lastName')}
          value={lastName}
          onChangeText={handleLastNameChange}
          error={lastNameError}
          maxLength={50}
          autoCapitalize="words"
        />
      </View>

      {/* Buttons */}
      <View style={styles.buttons}>
        <Button
          title={t('common.buttons.cancel')}
          onPress={onCancel}
          variant="outline"
          style={styles.buttonHalf}
        />
        <Button
          title={t('common.buttons.save')}
          onPress={handleSave}
          loading={saving}
          disabled={saving || uploadingAvatar || !isDirty}
          style={styles.buttonHalf}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  title: {
    ...Typography.section,
    color: Colors.textDark,
    marginBottom: 24,
  },
  avatarContainer: {
    alignSelf: 'center',
    marginBottom: 24,
    position: 'relative',
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: Colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: Colors.white,
    ...Shadows.cardHover,
  },
  avatarImage: {
    width: 90,
    height: 90,
    borderRadius: 45,
  },
  avatarText: {
    fontFamily: Fonts.display,
    fontSize: 36,
    fontWeight: '400',
    color: Colors.textDark,
  },
  cameraOverlay: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: Colors.textDark,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: Colors.white,
  },
  form: {
    gap: 16,
    marginBottom: 24,
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
  },
  buttonHalf: {
    flex: 1,
  },
});
